import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subscription, catchError, concatMap, from, mergeMap, tap, toArray } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { ExternalShareService } from 'src/app/services/external-share.service';
import { MFilesService } from 'src/app/services/mfiles.service';
import { PageService } from 'src/app/services/page.service';

@Component({
	selector: 'mfiles-row',
	templateUrl: './table-row.component.html',
	styleUrls: ['./table-row.component.css']
})
export class TableRowComponent implements OnInit, OnDestroy {
	modalRef: BsModalRef;
	@ViewChild('coauthorModal') private coauthorModal: TemplateRef<any>;
	@ViewChild('coauthorRequestModal') private coauthorRequestModal: TemplateRef<any>;
	@ViewChild('deleteDialog') private deleteDialog: TemplateRef<any>;
	@ViewChild('downloadProgressModal') private downloadProgressModal: TemplateRef<any>;
	@ViewChild('uploadProgressModal') private uploadProgressModal: TemplateRef<any>;

	loadingRelated = false;
	loadingDownload = false;

	favorites: any[] = [];

	@Input() view: any;
	@Input() item: any;
	@Input() inGrid = false;
	@Input() completedCriteriaField: boolean;
	@Input() completedField: boolean;
	@Input() completedEnabled: boolean;

	@Input() revisionsEnabled: boolean;
	@Input() coauthorEnabled: boolean;
	@Input() associateSelectedEnabled = false;
	@Input() removeFromShareEnabled = false;
	@Input() deleteEnabled = false;
	@Input() disableRelatedView = false;

	@Input() vaultProperties: any[];
	@Input() objectTypes: any;
	@Input() objectLists: any;

	@Input() icon: any;
	@Input() page: number;
	@Input() path: string;
	@Input() pageId = -1;
	@Input() widgetId = '';

	@Input() objectTypeIcons = {};

	@Input() automaticWorkflowTransitions = false;
	@Input() startingState: number;
	@Input() targetState: number;

	@Input() external = false;
	@Input() isAuthed = true;
	@Input() token: string;
	@Input() permissions = [];
	@Input() autoSelect = false;
	@Input() doNotDownload = false;

	@Output() uploaderOpened = new EventEmitter<any>();
	@Output() deleteClicked = new EventEmitter<any>();
	@Output() associateObjectType = new EventEmitter<number>();
	@Output() associateObject = new EventEmitter<number>();
	@Output() externalObjectSelected = new EventEmitter<any>();
	@Output() loadIcon = new EventEmitter<number>();
	@Output() loadView = new EventEmitter<number>();
	@Output() externalFileRemoved = new EventEmitter<number>();
	@Output() startLoad = new EventEmitter<any>();
	@Output() loadShare = new EventEmitter<any>();
	@Output() downloadCancelled = new EventEmitter<any>();


	completeCriteria = [];

	scrollListener: Function;

	coauthorItem: any;

	relatedLoaded = false;
	files = [];
	related = [];
	relatedTypeShown = {};
	showRelated = false;
	@Input() relatedParents = [];

	selectedItemId: number;
	selectedItemType: number;
	selectedItemVersion: number;
	itemSelectedChangedSubscription: Subscription;

	deleteName = '';
	deleteType: number;
	deleteItem: number;

	coauthorRequestEmail = '';
	coauthorRequestType: number;
	coauthorRequestItem: number;

	showingFiles = false;

	downloadProgress: number = 0;
	downloadSubscription: Subscription;
	docToDownload: any = { name: '', extension: '' };

	uploadsCommitting: boolean = false;
	uploadProgressStats: any = {};
	microserviceUploadSubscription: Subscription;

	constructor(
		public configService: ConfigurationService,
		private modalService: BsModalService,
		private mfilesService: MFilesService,
		private pageService: PageService,
		private myElement: ElementRef,
		private renderer: Renderer2,
		public authService: AuthService,
		private router: Router,
		private externalShareService: ExternalShareService,
		private toastr: ToastrService
	) {
	}

	ngOnInit() {
		this.subscribeToItemSelectedChanged();

		if (this.completedEnabled) {
			if (this.isElementInViewport(this.myElement.nativeElement)) {
				this.loadCompleted();
			} else {
				if (this.inGrid) {
					this.scrollListener = this.renderer.listen(this.myElement.nativeElement.closest('gridster-item'), 'scroll', (event) => {
						if (this.isElementInViewport(this.myElement.nativeElement)) {
							this.loadCompleted();
							this.scrollListener(); // destroys the scroll listener
						}
					});
				} else {
					this.scrollListener = this.renderer.listen(window, 'scroll', (event) => {
						if (this.isElementInViewport(this.myElement.nativeElement)) {
							this.loadCompleted();
							this.scrollListener(); // destroys the scroll listener
						}
					});
				}
			}
		}

		// Enable revisions if the user has clicked into a virtual folder that they have read/write access to
		if (this.view && this.view.hasAccessProperties && !this.external) {
			const pathSegments: string[] = this.view.path.split('/').filter(folder => folder.length > 0);

			// See Limitation comment above loadView() in view-widget.component.ts
			const firstGroupingLevelIndex: number = pathSegments.findIndex(segment => segment.match('L'));
			const firstGLAfterLastView: string = pathSegments[firstGroupingLevelIndex];
			const folderName: string = firstGLAfterLastView;
			if (this.view.rFolders.includes(folderName)) {
				this.revisionsEnabled = false;
			} else if (this.view.rwFolders.includes(folderName) || this.view.rwdFolders.includes(folderName)) {
				this.revisionsEnabled = true;
			}
		} else if (this.view && !this.view.hasAccessProperties && !this.external) {
			// TODO: Check content profile permissions to see if user has Download + Annotate or Delete permissions to class of current row object
			this.revisionsEnabled = this.item.annotate;
			this.deleteEnabled = this.item.delete;
		}
	}

	ngOnDestroy() {
		if (this.scrollListener) {
			this.scrollListener();
		}
		if (this.itemSelectedChangedSubscription) {
			this.itemSelectedChangedSubscription.unsubscribe();
		}
		if (this.downloadSubscription) {
			this.downloadSubscription.unsubscribe();
		}
	}

	subscribeToItemSelectedChanged() {
		this.itemSelectedChangedSubscription = this.pageService.itemSelectedChanged.subscribe((value: any) => {
			this.selectedItemId = value.id;
			this.selectedItemType = value.type;
			this.selectedItemVersion = value.version ? value.version : -1;
		});
	}

	isMetadataOrViewerPresent() {
		const widgets = this.pageService.getActivePageWidgets();
		let widgetFound = false;
		if (widgets) {
			for (const widget of widgets) {
				if (widget.type === 'viewer' || widget.type === 'metadata') {
					widgetFound = true;
				}
			}
		}

		return widgetFound;
	}

	fileClicked(item: any, fileIndex: number, e: any) {
		if (item.files) {
			this.showingFiles = !this.showingFiles;
		} else {
			let openFile = true;
			if (e.target.localName === 'em') {
				openFile = false;
			}
			if (this.automaticWorkflowTransitions && this.startingState && this.targetState) {
				for (const prop of this.item.properties) {
					if (prop.propertyDef === 39) { // workflow state
						if (this.startingState === prop.value.lookup.item) {
							this.mfilesService.updateSingleProperty(39, 9, this.targetState, item.type, item.id).subscribe((result) => {
								if (openFile) {
									this.openFile(item.id, item.type, fileIndex, item.version);
								}
							});
						} else if (openFile) {
							this.openFile(item.id, item.type, fileIndex, item.version);
						}
						break;
					}
				}
			} else if (openFile) {
				this.openFile(item.id, item.type, fileIndex, item.version);
			}
		}
	}

	openFile(itemId: number, type: number, fileIndex: number, version: number) {
		if (this.external) {
			this.externalObjectSelected.emit({ type, id: itemId, fileIndex, version });
		} else {
			// Determine if there is a metadata or viewer  widget on the page
			const widgetFound = this.isMetadataOrViewerPresent();

			// If there is, emit that a document selection has occurred so the widgets can retrieve their data
			if (widgetFound) {
				// Set additional context for workflow state and class
				let workflowState;
				let itemClass;
				for (const prop of this.item.properties) {
					if (prop.propertyDef === 39) { // workflow state
						workflowState = prop.value.serializedValue;
					} else if (prop.propertyDef === 100) { // class
						itemClass = prop.value.serializedValue;
					}
				}

				const context = {
					itemId: itemId,
					type: type,
					fileIndex: fileIndex,
					workflowState: workflowState,
					itemClass: itemClass
				};
				this.pageService.documentSelectionOccurred.emit(context);
			} else if (type === 0 || type === 9) {
				// If not, open the viewer
				if (this.page) {
					this.pageService.setPageEditModeChanged({ pageId: this.page, edit: false });
					this.router.navigate(['file', itemId, fileIndex], { queryParams: { sourcePage: this.page, path: this.path } });
				} else {
					this.router.navigate(['file', itemId, fileIndex]);
				}


				// In case we need to open in new tab in future
				// window.open(window.location.origin + '/#/file/' + fileId + query, '_blank');
			}
		}
	}

	subExternalObjectSelected(objectInfo: any) {
		this.externalObjectSelected.emit({
			type: objectInfo.type,
			id: objectInfo.id,
			fileIndex: objectInfo.fileIndex,
			version: objectInfo.version
		});
	}

	download(item: any, fileIndex: number, multiFileDoc?: boolean) {
		if (multiFileDoc === true) {
			item.files[fileIndex]['downloading'] = true;
		} else {
			this.loadingDownload = true;
		}

		let itemType = 0;
		let itemId = item;
		let version = -1;
		if (item.type) {
			itemType = item.type;
		}
		if (item.id) {
			itemId = item.id;
		}
		if (item.version) {
			version = item.version;
		}
		if (this.external) {
			this.externalShareService.download(this.token, itemType, itemId, fileIndex, version).subscribe(
				response => {
					if (response.type === 3) {
						this.downloadProgress = Math.floor(100 * (response.loaded / response.total));
					}
					if (response.type === 4 && response.body) {
						saveAs(response.body, response.headers.get('filename'));
						this.loadingDownload = false;

						if (multiFileDoc === true) {
							item.files[fileIndex]['downloading'] = false;
						}

						this.cancelDownload();
					}
				},
				err => {
					this.toastr.error('An error occurred. Please try again later.');
					this.loadingDownload = false;

					if (multiFileDoc === true) {
						item.files[fileIndex]['downloading'] = false;
					}
				});
		} else {
			if (this.pageId && this.widgetId) {
				this.mfilesService.pageDownload(itemType, itemId, -1, fileIndex, this.pageId, this.widgetId).subscribe(
					response => {
						saveAs(response.body, response.headers.get('filename'));
						this.loadingDownload = false;

						if (multiFileDoc === true) {
							item.files[fileIndex]['downloading'] = false;
						}
					},
					err => {
						this.toastr.error('An error occurred. Please try again later.');
						this.loadingDownload = false;

						if (multiFileDoc === true) {
							item.files[fileIndex]['downloading'] = false;
						}
					});
			} else {
				this.mfilesService.download(itemType, itemId, -1, fileIndex).subscribe(
					response => {
						saveAs(response.body, response.headers.get('filename'));
						this.loadingDownload = false;

						if (multiFileDoc === true) {
							item.files[fileIndex]['downloading'] = false;
						}
					},
					err => {
						this.toastr.error('An error occurred. Please try again later.');
						this.loadingDownload = false;

						if (multiFileDoc === true) {
							item.files[fileIndex]['downloading'] = false;
						}
					});
			}
		}
	}

	cancelDownload() {
		this.downloadCancelled.emit(this.item);
	}

	// This method is called using the Download button on Document Collections
	downloadDocumentCollection(item: any) {
		this.loadingDownload = true;
		if (this.external) {
			this.download(item, 0);
		} else {
			if (this.pageId && this.widgetId) {
				this.mfilesService.pageDownloadDocCollection(item, this.pageId, this.widgetId).subscribe({
					next: (response) => {
						saveAs(response.body, response.headers.get('filename'));
					},
					error: (err) => {
						this.toastr.error('An error occurred while attempting to download all documents from document collection.');
						console.error(err);
					},
					complete: () => {
						this.loadingDownload = false;
					}
				});
			} else {
				this.mfilesService.downloadDocCollection(item).subscribe({
					next: (response) => {
						saveAs(response.body, response.headers.get('filename'));
					},
					error: (err) => {
						this.toastr.error('An error occurred while attempting to download all documents from the document collection.');
						console.error(err);
					},
					complete: () => {
						this.loadingDownload = false;
					}
				});
			}
		}
	}

	downloadMultiFileDocument(item: any) {
		this.loadingDownload = true;
		if (this.external) {
			if (item.size) {
				if (item.size >= this.mfilesService.maxDownloadSize) {
					this.openModal(this.downloadProgressModal, 'md');
					this.docToDownload = item;
				}
			}

			this.downloadSubscription = this.externalShareService.downloadMultiFileDocument(this.token, item).subscribe({
				next: (response) => {
					if (response.type === 3) {
						this.downloadProgress = Math.floor(100 * (response.loaded / response.total));
					}
					if (response.type === 4 && response.body) {
						saveAs(response.body, response.headers.get('filename'));
						this.loadingDownload = false;
						this.cancelDownload();
					}
				},
				error: (err) => {
					this.toastr.error('An error occurred. Please try again later.');
					this.loadingDownload = false;
				}
			});
		} else {
			if (this.pageId && this.widgetId) {
				this.mfilesService.pageDownloadMultiFileDocument(item, this.pageId, this.widgetId).subscribe({
					next: (response) => {
						saveAs(response.body, response.headers.get('filename'));
					},
					error: (err) => {
						this.toastr.error('An error occurred while attempting to download all documents from the multi-file document.');
						console.error(err);
						this.loadingDownload = false;
					},
					complete: () => {
						this.loadingDownload = false;
					}
				});
			} else {
				this.mfilesService.downloadMultiFileDocument(item).subscribe({
					next: (response) => {
						saveAs(response.body, response.headers.get('filename'));
					},
					error: (err) => {
						this.toastr.error('An error occurred while attempting to download all documents from the multi-file document.');
						console.error(err);
						this.loadingDownload = false;
					},
					complete: () => {
						this.loadingDownload = false;
					}
				});
			}
		}
	}

	openModal(template: TemplateRef<any>, size: string) {
		this.modalRef = this.modalService.show(
			template,
			{ class: 'modal-' + size, backdrop: 'static', animated: false }
		);
	}

	addCriteria() {
		this.completeCriteria.push({ id: -1, comparison: 'eq', value: '' });
	}

	removeCriteria(i: number) {
		this.completeCriteria.splice(i, 1);
	}

	criteriaFieldChanged(e: any, i: number) {
		const fieldId = e.id;
		this.checkCriteriaField(fieldId);

		this.completeCriteria[i].value = '';
	}

	checkCriteriaField(fieldId: number) {
		if (this.vaultProperties && !this.objectTypes[fieldId]) {
			for (const property of this.vaultProperties) {
				if (property.id === fieldId) {
					if (!this.objectTypes[fieldId]) {
						this.objectTypes[fieldId] = property.dataType;
					}
					if (property.dataType === 9 || property.dataType === 10) {
						this.mfilesService.getValueListOptions(property.valueList).subscribe((result) => {
							this.objectLists[fieldId] = result.body['Items'];
						});
					}
					break;
				}
			}
		}
	}

	setCriteria() {
		this.modalRef.hide();
		for (const criteria of this.completeCriteria) {
			const dataType = this.objectTypes[criteria.id];
			if (dataType === 5 || dataType === 6 || dataType === 7) {
				criteria.value = criteria.value.getTime();
			}
		}

		this.item.loaded = false;
		this.mfilesService.updateCompleteCriteria(JSON.stringify(this.completeCriteria), 0, this.item.id).subscribe((response) => {
			const result: any = response.body;
			for (let i = 0; i < result.properties.length; i++) {
				const prop = result.properties[i];
				if (prop.propertyDef === this.completedField) {
					this.item.completed = prop.value.value;
				} else if (prop.propertyDef === this.completedCriteriaField) {
					this.item.completeCriteria = prop.value.value;
					this.completeCriteria = JSON.parse(prop.value.value);
				}
				this.item.loaded = true;
			}
		}, (err: any) => {
			this.item.loaded = true;
			this.item.error = true;
		});
	}

	loadCompleted() {
		this.item.loaded = false;
		this.mfilesService.getCompletedProperties(this.item).subscribe((response) => {
			this.item = response.body;

			this.completeCriteria = JSON.parse(this.item.completeCriteria);

			for (const criterion of this.completeCriteria) {
				this.checkCriteriaField(criterion.id);
				const dataType = this.objectTypes[criterion.id];
				if (dataType === 5 || dataType === 6 || dataType === 7) {
					criterion.value = new Date(criterion.value);
				}
			}
			this.checkCriteriaField(-1);

			this.item.loaded = true;
			this.item.error = false;
		}, (err: any) => {
			this.item.loaded = true;
			this.item.error = true;
		});
	}

	isElementInViewport(el: HTMLElement) {
		const rect = el.getBoundingClientRect();

		return (
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom - 100 <= (window.innerHeight || document.documentElement.clientHeight) &&
			rect.right <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	getRelated() {
		this.loadingRelated = true;

		if (this.relatedLoaded) {
			this.showRelated = true;

			this.loadingRelated = false;
		} else {
			this.relatedParents.push({ type: this.item.type, id: this.item.id, version: this.item.version });
			this.externalShareService.getRelated(this.token, this.item.type, this.item.id, this.item.version).subscribe((result: any) => {
				this.relatedLoaded = true;
				this.files = result.files;
				this.related = [];

				if (result.collectionMembers && result.collectionMembers.length > 0) {
					this.related.push({ type: 0, typeName: 'Collection members', objects: [...result.collectionMembers] });
				}

				if (this.files.length > 1 || this.related.length > 0) {
					for (const relType of this.related) {
						this.relatedTypeShown[relType.type] = false;
						for (const rel of relType.objects) {
							if (this.isInRelatedParent(rel)) {
								rel.expanded = true;
							}
						}
					}
					this.showRelated = true;

					this.loadingRelated = false;
				} else {
					this.loadingRelated = false;
				}
			});
		}
	}

	hideRelated() {
		this.showRelated = false;
	}

	hideShowRelType(type: number) {
		this.relatedTypeShown[type] = !this.relatedTypeShown[type];
	}

	getColCount() {
		let colCount = 3; // related, download, and shareLink are always visible
		if (this.completedEnabled) {
			colCount = colCount + 1;
		}
		colCount = colCount + this.view.viewContents.columns.length;
		if (this.revisionsEnabled) {
			colCount = colCount + 1;
		}
		if (this.coauthorEnabled) {
			colCount = colCount + 1;
		}
		return colCount;
	}

	isInRelatedParent(rel: any) {
		let inParent = false;
		for (const parent of this.relatedParents) {
			if (rel.type === parent.type && rel.id === parent.id && rel.version === parent.version) {
				inParent = true;
				break;
			}
		}
		return inParent;
	}

	uploadRevision(itemId: number, fileIndex: number) {
		this.uploaderOpened.emit({ itemId, fileIndex });
	}

	delete(name: string, itemType: number, itemId: number) {
		this.deleteClicked.emit({ name, itemType, itemId });
	}

	openVerifyDelete(name: string, itemType: number, itemId: number) {
		this.deleteName = name;
		this.deleteType = itemType;
		this.deleteItem = itemId;
		this.openModal(this.deleteDialog, 'md');
	}

	deleteObject() {
		this.modalRef.hide();
		this.view.loading = true;
		this.mfilesService.pageDeleteObject(this.deleteType, this.deleteItem, true, this.pageId, this.widgetId).subscribe((result) => {
			this.deleteType = -1;
			this.deleteItem = -1;
			this.emitLoadView(this.view);
		});
	}

	emitLoadView(view: any) {
		this.loadView.emit(view);
	}

	isCoauthorItem(item: any) {
		return this.mfilesService.coauthorExtensions.indexOf(item.extension) > -1;
	}

	coauthor(item: any) {
		if (this.isAuthed) {
			if (item.coauthored) {
				this.coauthorItem = item;
				this.openModal(this.coauthorModal, 'md');
			} else {
				this.mfilesService.startCoauthoring(item.id);
				item.coauthored = !item.coauthored;
			}
		} else {
			this.coauthorRequestEmail = '';
			this.coauthorRequestType = item.type;
			this.coauthorRequestItem = item.id;
			this.openModal(this.coauthorRequestModal, 'md');
		}
	}

	requestCoauthor() {
		this.modalRef.hide();
		this.externalShareService.requestCoauthor(this.token, this.coauthorRequestItem, this.coauthorRequestType, this.coauthorRequestEmail).subscribe((result) => {
			this.coauthorRequestEmail = '';
			this.coauthorRequestType = -1;
			this.coauthorRequestItem = -1;
			this.toastr.success('CoAuthor Request Successful');
		});
	}

	resumeCoauthor() {
		this.mfilesService.continueCoauthoring(this.coauthorItem.id);
	}

	undoCoauthor() {
		this.modalRef.hide();
		this.mfilesService.undoCoauthoring(this.coauthorItem.id).subscribe((result) => {
			this.coauthorItem.coauthored = !this.coauthorItem.coauthored;
			this.coauthorItem = undefined;
		}, (err) => {
			this.mfilesService.undoCoauthoringLink(this.coauthorItem.id);
			this.coauthorItem.coauthored = !this.coauthorItem.coauthored;
			this.coauthorItem = undefined;
		});
	}

	checkinCoauthor() {
		this.modalRef.hide();
		this.mfilesService.endCoauthoring(this.coauthorItem.id).subscribe((result) => {
			this.coauthorItem.coauthored = !this.coauthorItem.coauthored;
			this.coauthorItem = undefined;
		}, (err) => {
			this.mfilesService.endCoauthoringLink(this.coauthorItem.id);
			this.coauthorItem.coauthored = !this.coauthorItem.coauthored;
			this.coauthorItem = undefined;
		});
	}

	getDisplayValueForColumn(column: any): string {
		const columnId = column.id;
		let columnProp = null;

		for (const prop of this.item.properties) {
			if (prop.propertyDef === columnId) {
				columnProp = prop;
				break;
			}
		}

		if (columnProp != null && columnProp.value.displayValue != null && columnProp.value.displayValue.length > 0) {
			return columnProp.value.displayValue;
		} else {
			return '-';
		}
	}

	getItemSize() {
		let size = 0;

		for (const prop of this.item.properties) {
			if (prop.propertyDef === 31) {
				size = prop.value.value;
				break;
			}
		}
		return size;
	}

	associate(item: any) {
		this.associateObjectType.emit(item.type);
		this.associateObject.emit(item.id);

		this.pageService.setItemSelectedChanged({ id: item.id, type: item.type });
	}

	getIcon(type: number) {
		this.loadIcon.emit(type);
	}

	copyToClipboard(type: number, id: number) {
		const oldUrl = window.location.href;
		let newUrl;

		if (/[?&]id\s*=/.test(oldUrl)) {
			newUrl = oldUrl.replace(/(?:([?&])id\s*=[^?&]*)/, "$1id=" + id + '&type=' + type);
		} else if (/\?/.test(oldUrl)) {
			newUrl = oldUrl + "&id=" + id + '&type=' + type;
		} else {
			newUrl = oldUrl + "?id=" + id + '&type=' + type;
		}

		if (!navigator.clipboard) {
			this.fallbackCopyTextToClipboard(newUrl);
			return;
		}

		navigator.clipboard.writeText(newUrl).then(function () {
			this.toastr.success('Copied to clipboard.');
		}, function (err) {
			this.toastr.error('Unable to copy to clipboard.');
		});
	}

	fallbackCopyTextToClipboard(text) {
		const textArea = document.createElement("textarea");
		textArea.value = text;

		// Avoid scrolling to bottom
		textArea.style.top = "0";
		textArea.style.left = "0";
		textArea.style.position = "fixed";

		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();

		try {
			this.toastr.success('Copied to clipboard.');
		} catch (err) {
			this.toastr.error('Unable to copy to clipboard.');
		}

		document.body.removeChild(textArea);
	}

	removeFile(fileId: number) {
		this.externalFileRemoved.emit(fileId);
	}

	closeModal() {
		if (this.modalRef) {
			this.modalRef.hide();
			this.modalRef = null;
		}
	}

	reviseFile(item: any, fileIndex: number, e: any) {
		this.startLoad.emit();

		const revisionFile: File = e.target.files[0];

		this.mfilesService.isMicroserviceAvailable().subscribe((available) => {
			if (available) {
				this.uploadProgressStats[revisionFile.name] = 0;	// Init the progress stats for this file
				this.mfilesService.microserviceUploadProgress = this.uploadProgressStats;
				this.openModal(this.uploadProgressModal, 'md');

				this.microserviceUploadSubscription = from([''])
					.pipe(
						tap(() => this.authService.isUploading = true),
						concatMap((props) => this.externalShareService.prepareUploadRevision(this.token, item.id, fileIndex)),								// Makes the prepareUploadRevision calls in order and returns the result in order.
						mergeMap((result, index) => this.mfilesService.microserviceUpload(result, e.target.files[0], true)
							.catch(err => {																								// If an error occurs while the file is uploading to the microservice, this catches it and ensures the rest of the uploads don't get canceled.
								if (err.file) {
									this.uploadProgressStats[err.file.name] = 'ERROR';													// UI looks for this value and uses it to determine if the error message should be shown. Don't remove it.
								}
								return EMPTY;
							}), 3),																										// Starts uploading the files to the microservice. The 3 tells angular to limit the number of concurrent uploads to 3.
						mergeMap((result) => result, 1),																				// Intermediate step since microserviceUpload() returns a Promise with an Observable in it. This just returns the enclosed Observable.
						toArray(),																										// Merge all of the microserviceUpload results into an array.
						tap(() => {
							this.uploadsCommitting = true;
						}),
						mergeMap(() => this.externalShareService.postUploadRevision(this.token, item.id, fileIndex)			// Calls the postUploadRevision with all of the new objects in M-Files.
							.pipe(
								catchError(err => {
									this.toastr.error('An error occurred while attempting to upload your files into M-Files. Please try again later.');
									return EMPTY;
								})
							), 1),
						tap(() => {
							this.uploadsCommitting = false;
							this.authService.isUploading = false;
							this.mfilesService.emitUploadProgress('all', 100);
						}))								// The upload to M-Files is now 100% complete.
					.subscribe({
						next: (res) => { },
						error: (err) => { },
						complete: () => {

							setTimeout(() => {
								this.closeModal();

								this.loadShare.emit();
								this.mfilesService.microserviceUploadProgress = {};
								this.uploadProgressStats = {};
							}, 1500);
						}
					});
			} else {
				this.externalShareService.uploadRevision(this.token, item.id, fileIndex, e.target.files[0]).subscribe(() => {
					this.loadShare.emit();
				});
			}
		});
	}

	docChecked(doc: any, event: any) {
		doc.checkedForDownload = event.target.checked;
	}
}
